import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

const STORAGE_KEY = "tappanon:scroll-positions";
const MAX_ENTRIES = 50;
/** How long a restore keeps working towards the saved spot while the page loads (cached lists arrive at once; refetches take longer). */
const RESTORE_TIMEOUT_MS = 3000;
/** Keys that scroll the page or move focus. Anything else, like the modifier still held from Alt+Left, leaves a restore running. */
const SCROLL_KEYS = new Set(["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", " ", "Tab"]);

type Positions = Record<string, number>;

/** This document's latest map, so back navigation keeps working when sessionStorage is blocked or full. */
let memory: Positions | null = null;

function readPositions(): Positions {
  if (memory) return { ...memory };
  try {
    // Object.entries throws on a stored `null`, which lands in the catch below.
    const parsed = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}") as Record<string, unknown>;
    // Keys that contain "?" came from the first release, which stored the query string (activation tokens).
    return Object.fromEntries(
      Object.entries(parsed).filter(([k, y]) => !k.includes("?") && typeof y === "number" && Number.isFinite(y)),
    ) as Positions;
  } catch {
    return {};
  }
}

function savePositions(positions: Positions) {
  const keys = Object.keys(positions);
  for (const old of keys.slice(0, Math.max(0, keys.length - MAX_ENTRIES))) delete positions[old];
  memory = positions;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(positions));
  } catch {
    // Storage full or blocked: positions last for this page session only.
  }
}

function writePosition(key: string, y: number) {
  const positions = readPositions();
  delete positions[key];
  positions[key] = y;
  savePositions(positions);
}

const maxScroll = () => document.documentElement.scrollHeight - window.innerHeight;

/**
 * A history entry's storage key. The router gives every freshly loaded page the
 * key "default", so the path keeps two such pages from sharing a position. The
 * query is left out: it can carry a secret (`/activate-account?token=…`).
 */
const entryKey = (key: string, pathname: string) => `${key}|${pathname}`;

let documentStarted = false;

/**
 * True once per document, on its first navigation, when the browser loaded it as
 * a new visit (a link or typed URL). The router reports that load as POP with the
 * key "default", the same as a reload or a back from another site, which do restore.
 */
function startsNewVisit() {
  if (documentStarted) return false;
  documentStarted = true;
  try {
    const type = (performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined)?.type;
    return type === "navigate" || type === "prerender";
  } catch {
    return false;
  }
}

const isTextField = (target: EventTarget | null) =>
  target instanceof HTMLElement && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName));

/**
 * A new page replaced the link that had focus, so focus fell back to the body.
 * Start keyboard and screen reader focus at the top of the new content instead,
 * without scrolling. Focus the member (or the page) put somewhere is left alone.
 */
function focusNewPage() {
  const active = document.activeElement;
  if (active && active !== document.body) return;
  const main = document.querySelector("main");
  if (!main) return;
  if (!main.hasAttribute("tabindex")) {
    main.setAttribute("tabindex", "-1");
    main.addEventListener("blur", () => main.removeAttribute("tabindex"), { once: true });
  }
  main.focus({ preventScroll: true });
}

/**
 * Scroll behaviour for client-side navigation, which the browser can't handle on
 * its own: it restores a history entry's position before the page has rendered,
 * so a long list comes back capped near the top.
 *
 * - Back/forward (POP): return to where the member was on that entry. While the
 *   page loads, keep moving as far towards that spot as it allows, which also
 *   keeps an infinite list loading its next page. Scrolling, a press or a scroll
 *   key hands control back; a sideways swipe or a modifier key doesn't.
 *   An entry never scrolled was at the top, so it comes back at the top.
 * - A new page (pathname change): start at the top, with focus at the start of
 *   the new content if the link that was focused has gone.
 * - Same page, different query or hash (tabs, filters): leave the position alone.
 * - A new full-page visit starts at the top; a reload or a back from another site restores.
 */
export default function ScrollManager() {
  const { key, pathname, search } = useLocation();
  const navigationType = useNavigationType();
  const keyRef = useRef(entryKey(key, pathname));
  const pathRef = useRef<string | null>(null);
  const restoringRef = useRef(false);

  useEffect(() => {
    savePositions(readPositions()); // rewrites storage without any legacy query-string keys
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    return () => {
      window.history.scrollRestoration = previous;
    };
  }, []);

  // Remember the position of whichever entry is showing, as the member scrolls.
  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      if (restoringRef.current || frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        writePosition(keyRef.current, Math.round(window.scrollY));
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(frame);
    };
  }, []);

  useLayoutEffect(() => {
    keyRef.current = entryKey(key, pathname);
    const newVisit = startsNewVisit();
    const previousPath = pathRef.current;
    const samePage = previousPath === pathname;
    pathRef.current = pathname;

    if (navigationType !== "POP") {
      if (!samePage) {
        window.scrollTo(0, 0);
        focusNewPage();
      }
      return;
    }

    if (newVisit) {
      const positions = readPositions();
      delete positions[keyRef.current];
      savePositions(positions);
    }
    const target = readPositions()[keyRef.current];
    if (target === undefined) {
      // Never scrolled on this entry, so it was at the top; don't keep the last page's offset.
      if (previousPath !== null && !samePage) window.scrollTo(0, 0);
      return;
    }

    restoringRef.current = true;
    const started = performance.now();
    let frame = 0;
    const finish = () => {
      restoringRef.current = false;
      cancelAnimationFrame(frame);
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("pointerdown", finish);
      window.removeEventListener("touchstart", finish);
      window.removeEventListener("keydown", onKey);
    };
    // The member took over; don't yank them back. Trackpad momentum from a
    // sideways back swipe is mostly horizontal, so only vertical wheels count.
    const onWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) finish();
    };
    const onKey = (event: KeyboardEvent) => {
      if (!SCROLL_KEYS.has(event.key) || (event.key !== "Tab" && isTextField(event.target))) return;
      finish();
    };
    const attempt = () => {
      const reachable = Math.min(target, Math.max(0, maxScroll()));
      if (Math.round(window.scrollY) !== reachable) window.scrollTo(0, reachable);
      if (reachable >= target || performance.now() - started > RESTORE_TIMEOUT_MS) {
        finish();
        return;
      }
      frame = requestAnimationFrame(attempt);
    };
    window.addEventListener("wheel", onWheel, { passive: true });
    window.addEventListener("pointerdown", finish, { passive: true });
    window.addEventListener("touchstart", finish, { passive: true });
    window.addEventListener("keydown", onKey);
    attempt();
    return finish;
  }, [key, pathname, search, navigationType]);

  return null;
}
